import React, { useState, useCallback, useEffect } from "react";
import { useStore } from "./store";
import { Masthead, Button, Grid, Typography, TextField, Box, Stack, Card, CardContent, Paper,  Divider, Pagination, BarChart} from '@barracuda-internal/bds-core';
import { Table as BDSTable } from '@barracuda-internal/bds-core/dist/Table';
import {
  Table, TableBody, TableCell,
  TableContainer, TableRow
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "./api";
import { API_BASE_URL } from "./settings";

export default function Scan() {
  const { state } = useStore();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [domainError, setDomainError] = useState(false);
  const [domainHelper, setDomainHelper] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [10, 20];

  const navigate = useNavigate ? useNavigate() : null;


  // Input sanitizer: returns true when input looks like an allowed domain or URL
  const sanitizeInput = (val) => {
    if (!val) return false;
    const s = String(val).trim();
    // Block obvious dangerous content: script tags and dangerous schemes
    const lowered = s.toLowerCase();
    if (/<\s*script\b/.test(lowered)) return false;
    if (/^\s*(?:javascript|data|file|vbscript)\s*:/i.test(s)) return false;

    // Block standalone shell/command invocations like `ls`, `rm`, `sh`, etc.
    // Require a trailing space or end-of-string to avoid matching substrings
    // inside normal hostnames (e.g. 'searchhounds').
    const cmdPattern = /^\s*(?:ls|cat|rm|sudo|sh|bash|python|curl|wget|nc|ncat|telnet|perl|php|node|echo|touch)(?:\s|$)/i;
    if (cmdPattern.test(s)) return false;

    // Basic hostname or URL checks: allow hostnames like example.com and URLs with http/https
    try {
      // if it's a URL (starts with scheme://), ensure scheme is http/https
      if (/^\w+:\/\//.test(s)) {
        const u = new URL(s);
        const scheme = (u.protocol || '').toLowerCase();
        if (scheme !== 'http:' && scheme !== 'https:' && scheme !== 'null') return false;
        // hostname must be present and use sane characters
        if (!u.hostname || /[^\w.-]/.test(u.hostname)) return false;
        return true;
      }
    } catch (e) {
      return false;
    }

    // For non-URL inputs, block dangerous shell metacharacters and command
    // substitution characters. This runs after URL detection so valid URLs
    // containing characters like '&' or '=' in query params are allowed.
    if (/[;`$<>\n\r(){}|]/.test(s)) return false;
    // Plain hostname: allow letters, numbers, hyphen and dots (simple FQDN check)
    const asciiHost = new URL(`http://${s}`).hostname;
    
if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(asciiHost)) return true;

    return false;
  };

  // Start scan: session-based auth with CSRF
  const startScan = useCallback(async () => {
    // Validate/sanitize before sending
    if (!domain || !sanitizeInput(domain)) {
      setDomainError(true);
      setDomainHelper('Please enter correct domain :https://example.com or example.com');
      return;
    }

    setDomainError(false);
    setDomainHelper('');
    setLoading(true);

    try {
      const payload = { domain };

      const resp = await api.post(
        `${API_BASE_URL}/api/scan-requests/`,
        payload,
        { withCredentials: true }
      );

      const uuid = resp.data?.scan_result_uuid || null;
      if (uuid) navigate(`/scan-result/${uuid}`);
      else alert("Scan started but no result UUID was returned. Please try again.");
    } catch (e) {
      // Prefer structured backend message when available, otherwise show a friendly helper
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      if (status === 400 && (data.error === 'invalid_domain' || typeof data.detail === 'string')) {
        setDomainError(true);
        setDomainHelper(typeof data.detail === 'string' ? data.detail : 'Please enter a valid domain');
        setLoading(false);
        return;
      }

      // Network or unexpected axios error: show friendly helper instead of throwing
      if (e?.isAxiosError) {
        setDomainError(true);
        setDomainHelper('Please enter a valid domain');
        setLoading(false);
        return;
      }

      // Other unexpected errors: fallback to alert but do not rethrow
      console.error(e);
      alert("An error occurred while starting the scan.");
    } finally {
      setLoading(false);
    }
  }, [domain, state, navigate]);




  // The existing UI data (kept intact)
  const vulnList = Array.from({ length: 21 }).map((_, i) => {
    const items = [
      { title: "SQL injection", path: "demo.com/v2/payment, 2 more instances", severity: "high" },
      { title: "Content Security Policy (CSP) header not set", path: "demo.com/v2/payment", severity: "medium" },
      { title: "Cross-domain misconfiguration", path: "demo.com/v2/payment", severity: "medium" },
      { title: "Missing Anti-clickjacking header", path: "demo.com/v2/payment", severity: "medium" },
      { title: "Session ID in URL rewrite", path: "demo.com/v2/payment", severity: "medium" },
      { title: "Vulnerable JS library", path: "demo.com/v2/payment", severity: "low" },
      { title: "Cookie no HttpOnly flag", path: "demo.com/v2/payment, 2 more instances", severity: "low" },
      { title: "Cookie without SameSite attribute none", path: "demo.com/v2/payment, 2 more instances", severity: "low" },
      { title: "Cookie without SameSite attribute", path: "demo.com/v2/payment, 2 more instances", severity: "low" },
      { title: "Loosely scoped cookie", path: "demo.com/v2/payment, 2 more instances", severity: "low" }
    ][i % 10];
    return { id: i + 1, ...items };
  });

  const renderSeverityBars = (sev) => {
    const colored = sev === "high" ? 4 : sev === "medium" ? 3 : 2;
    const total = 4;
    const grey = total - colored;
    return (
      <Box sx={{ display: 'flex', gap: 0.35, alignItems: 'center' }}>
        {Array.from({ length: colored }).map((_, i) => (
          <Box key={`c-${i}`} sx={{ width: 10, height: 2.8, borderRadius: 0.4, bgcolor: sev === 'high' ? '#d32f2f' : sev === 'medium' ? '#ff9800' : '#fbc02d' }} />
        ))}
        {Array.from({ length: grey }).map((_, i) => (
          <Box key={`g-${i}`} sx={{ width: 10, height: 2.8, borderRadius: 0.4, bgcolor: '#e6e9ec' }} />
        ))}
      </Box>
    );
  };

  const total = vulnList.length;
  const pageCount = Math.max(1, Math.ceil(total / itemsPerPage));
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = Math.min(total, page * itemsPerPage);
  const visible = vulnList.slice(startIndex, endIndex);

  // ...existing code...
          // Fetch previous scans from backend
          const [previousScans, setPreviousScans] = useState([]);

          useEffect(() => {
            async function fetchScans() {
              try {
                const resp = await api.get(`${API_BASE_URL}/api/scan-results/`, { withCredentials: true });
                // resp.data is expected to be a list of scan results
                // Each scan result should have: uuid, scan (with domain), scanned_at, status
                // Flatten for table
                const scans = Array.isArray(resp.data) ? resp.data : resp.data.results || [];
                setPreviousScans(scans.map(s => {
                    const status = s.scan?.status || s.status;
                    let statusIcon = null;
                    if (status === 'completed') {
                      statusIcon = (
                        <span title="Completed" style={{ color: '#2e7d32', fontSize: 18, display: 'inline-flex', alignItems: 'center' }}>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#e8f5e9"/><path d="M6.5 10.5L9 13L14 8" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                      );
                    } else if (status === 'failed') {
                      statusIcon = (
                        <span title="Failed" style={{ color: '#d32f2f', fontSize: 18, display: 'inline-flex', alignItems: 'center' }}>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#ffebee"/><path d="M7 7L13 13M13 7L7 13" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round"/></svg>
                        </span>
                      );
                    } else {
                      statusIcon = (
                        <span title="In Progress" style={{ color: '#0288d1', fontSize: 18, display: 'inline-flex', alignItems: 'center' }}>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#e3f2fd"/><path d="M10 5V10L13 13" stroke="#0288d1" strokeWidth="2" strokeLinecap="round"/></svg>
                        </span>
                      );
                    }
                    return {
                      domain: s.scan?.domain || s.domain,
                      scanned_at: s.scanned_at ? new Date(s.scanned_at).toLocaleString() : '-',
                      status: statusIcon,
                      action: (
                        <Typography onClick={() => navigate(`/scan-result/${s.uuid}`)} sx={{ color: '#0b78d1', fontSize: '0.72rem', cursor: 'pointer' }}>View details</Typography>
                      )
                    };
                }));
              } catch (e) {
                setPreviousScans([]);
              }
            }
            fetchScans();
          }, []);

  // Keep page valid when itemsPerPage changes or total changes
  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [pageCount, page]);

  return (
    <Grid container spacing={0} sx={{ bgcolor: '#ffffff', height: '100vh', overflow: 'hidden' }}>
      <Grid item xs={12}>
        <Masthead
          pageTitle="Application Security Insights"
          productTitle="WAF-as-a-Service"
          rightContent={<Button bdsType="interactiveEmphasis" data-action="try" onClick={() => window.open('https://waas.barracudanetworks.com/start-trial/', '_blank', 'noopener,noreferrer')}>Try WAF-as-a-Service now</Button>}
        />
      </Grid>

      {/* LEFT - fullwhite background */}
      <Grid item xs={6}>
        <Box sx={{
          p: 6,
          minHeight: 'calc(100vh - 120px)',
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#ffffff',
          overflow: 'hidden'
        }}>
          <Box sx={{ width: '100%', maxWidth: 600 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#111', fontWeight: 600, fontSize: '2rem', lineHeight: 1.2, mb: 2 }}>
              Start your free Analysis and take control of your web security
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.5, mb: 3 }}>
              Our automated scanner identifies common vulnerabilities and weaknesses, delivering clear, actionable insights to help you strengthen your website's security.
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <TextField
                placeholder="Enter domain"
                variant="outlined"
                size="small"
                value={domain}
                onChange={e => { setDomain(e.target.value); setDomainError(false); setDomainHelper(''); }}
                error={domainError}
                helperText={domainError ? domainHelper : ''}
                FormHelperTextProps={{ sx: { color: domainError ? '#1976d2' : '#1976d2', fontSize: '0.75rem', minHeight: '1.1rem' } }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { fontSize: '0.75rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cfd8dc' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' }
                }}
              />
              <Button
                onClick={() => { startScan().catch(err => { setDomainError(true); setDomainHelper('Please enter a valid domain'); }); }}
                disabled={loading || !domain}
                sx={{
                  mt: '6px',
                  width: 90,
                  height: 32,
                  backgroundColor: '#0b78d1',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  '&:hover': { backgroundColor: '#096bb8' },
                  '&.Mui-disabled': { backgroundColor: '#0b78d1', color: '#fff', opacity: 1 }
                }}
              >
                {loading ? "Starting..." : "Analyze"}
              </Button>
            </Stack>
            <Grid sx={{ mt: 4 }}>
              {/* Previous Scans Table */}
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
                Previous Scans
              </Typography>
              <Grid sx={{ maxHeight: 310, overflowY: 'auto' }}>
                <BDSTable rows={previousScans} headers={['Domain','Scanned At', 'Status', 'Action']} shouldHideTableHeadBorderBottom={false} customComponent={Paper}/>
              </Grid>
            </Grid>
          
          </Box>
        </Box>
      </Grid>

      <Grid item xs={6}>
        <Box
          sx={{
            p: 10,
            minHeight: 'calc(100vh - 70px)',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f3f5f7',
            overflow: 'hidden',
            width: '100%'      // right column fills full right area (blackish-white background)
          }}
        >
          {/* Top cards */}
          <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'flex-start', justifyContent: 'flex-start' }}>
            <Grid item xs={5} container direction="column" spacing={1}>
              <Grid item>
                <Card elevation={1}>
                  <CardContent sx={{ p: 1.1 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#666', mb: 0.4 }}>
                      Security posture score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography sx={{ fontSize: '1.45rem', fontWeight: 600 }}>49</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>of 100</Typography>
                      <Box sx={{ width: 30, height: 16, bgcolor: '#d32f2f', borderRadius: 0.4, ml: 0.3 }} />
                    </Box>
                    <Typography sx={{ color: '#0b78d1', fontSize: '0.68rem', mt: 0.3 }}>What's this?</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item>
                <Card elevation={1}>
                  <CardContent sx={{ p: 1.1 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#666', mb: 0.4 }}>Vulnerabilities detected</Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 600 }}>21</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Card elevation={1}>
                <CardContent sx={{ p: 1.1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>Vulnerabilities by severity</Typography>
                    <Typography sx={{ color: '#0b78d1', fontSize: '0.68rem', cursor: 'pointer' }}>View all ↓</Typography>
                  </Box>
                  <Box sx={{ height: 100, bgcolor: '#ffffff', borderRadius: 0.6 }} >
                    <BarChart
                                            chartData={{
                                              datasets: [
                                                {
                                                  label: 'Sales',
                                                  data: [
                                                    (6), // high
                                                    (4), // medium
                                                    (5), // low
                                                    (3), // info
                                                  ],
                                                  backgroundColor: [
                                                    '#1976d2',
                                                    '#1e88e5',
                                                    '#64b5f6',
                                                    '#90caf9'
                                                  ], // Bar fill colors (design tokens)
                                                  borderColor: [
                                                    '#1565c0',
                                                    '#1976d2',
                                                    '#1e88e5',
                                                    '#64b5f6'
                                                  ], // Bar border colors (design tokens)
                                                  borderWidth: 1
                                                }
                                              ], labels: ['High', 'Medium', 'Low', 'Info'],
                                            }}
                                            chartDimension={{ height: 120, width: '100%' }}
                                            emptyState={{
                                              icon: <div data-testid="empty-state" />,
                                              text: 'Too full to eat anymore twix'
                                            }}
                                            hasEntries={true}
                                            showFooter={false}
                                            barChartOptions={{
                                              plugins: { tooltip: { enabled: false } }, scales: {
                                                x: {
                                                  grid: {
                                                    display: false, // Removes vertical grid lines
                                                    drawOnChartArea: false // Removes grid lines only within the chart area
                                                  }
                                                },
                                                y: {
                                                  grid: {
                                                    display: false, // Removes horizontal grid lines
                                                    drawOnChartArea: false // Removes grid lines only within the chart area
                                                  }, border: {
                                                    display: false // This hides the X-axis line
                                                  }, ticks: {
                                                    display: false // This hides the Y-axis labels
                                                  }
                                                }
                                              }
                                            }}
                                          />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Vulnerabilities list */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 1,
              p: 0.9,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: '0 0 auto'
            }}
          >
            <Box sx={{ p: 0.9 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.6 }}>
                Vulnerabilities (21)
              </Typography>
              <Divider sx={{ mb: 0.4 }} />
            </Box>
            <TableContainer sx={{ flex: '0 0 auto', overflow: 'visible', maxHeight: 'none' }}>
               <Table size="small">
                 <TableBody>
                   {visible.length > 0 ? (
                     visible.map(v => (
                       <TableRow key={v.id} sx={{ '&:hover': { bgcolor: '#f7f8fa' } }}>
                         <TableCell sx={{ fontSize: '0.72rem', p: 0.5, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           <Typography sx={{ fontWeight: 600, fontSize: '0.60rem' }}>{v.title}</Typography>
                         </TableCell>

                         <TableCell sx={{ fontSize: '0.68rem', p: 0.5, color: '#777', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                           {v.path}
                         </TableCell>

                         <TableCell sx={{ fontSize: '0.68rem', p: 0.5, minWidth: 110 }}>
                           <Typography sx={{ fontSize: '0.66rem', mb: 0.3 }}>Severity: {v.severity}</Typography>
                           {renderSeverityBars(v.severity)}
                         </TableCell>

                         <TableCell align="right" sx={{ p: 0.5 }}>
                           <Typography sx={{ color: '#0b78d1', fontSize: '0.72rem', cursor: 'pointer' }}>View details</Typography>
                         </TableCell>
                       </TableRow>
                     ))
                   ) : (
                     <TableRow>
                       <TableCell colSpan={4} sx={{ textAlign: 'center', p: 2, bgcolor: '#000' }} />
                     </TableRow>
                   )}
                 </TableBody>
               </Table>
            </TableContainer>

              {/* Footer: pagination + items per page */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, pt: 0.25, p: 0 }}>
                <Pagination
                  itemsPerPage={itemsPerPage}
                  itemsPerPageDescription="items per page"
                  // itemsPerPageOptions={[
                  //   10,
                  //   20,
                  //   50
                  // ]}
                  onChange={function noRefCheck(){}}
                  onClickItemsPerPage={function noRefCheck(){}}
                  onClickItemsPerPageOption={function noRefCheck(){}}
                  onCloseItemsPerPage={function noRefCheck(){}}
                  page={page}
                  totalItems={pageCount}
                  disabled
                />
             </Box>
            </Paper>
        </Box>
      </Grid>
    </Grid>
  );
}